'use client';

import { clsx } from 'clsx';
import { FC, useEffect, useMemo, useState } from 'react';
import { NavigationOptions, SwiperOptions } from 'swiper/types';
import { IconSVG } from 'atoms/IconSVG';
import styles from './slider.module.css';

export enum NavigationTypes {
  DEFAULT = 'default',
  COMPACT = 'compact',
}

export const NavigationButtons: FC<{
  navigation: NavigationOptions;
  type: NavigationTypes;
  breakpoints: SwiperOptions['breakpoints'];
  classes: {
    container?: string;
    prev?: string;
    next?: string;
  };
}> = ({ navigation, type, breakpoints = {}, classes }) => {
  const getScreenWidth = () =>
    typeof window !== 'undefined' ? window.screen.availWidth : 0;

  const [screenWidth, setScreenWidth] = useState<number>(getScreenWidth());

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(getScreenWidth());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const config = useMemo<NavigationOptions>(() => {
    const breakpointsArray: [number, SwiperOptions][] = Object.entries(
      breakpoints,
    ).map(([key, value]) => [Number(key), value]);
    const isScreenMoreThanMaxBreakpoint =
      screenWidth >= Math.max(...breakpointsArray.map((item) => item[0]));

    return breakpointsArray.reduce<NavigationOptions>(
      (acc, [breakpoint, options], i, array) => {
        const lessThenNextBreakpoint = () => {
          const nextBreakpoint = array?.[i + 1]?.[0];

          return !!nextBreakpoint ? screenWidth < Number(nextBreakpoint) : true;
        };

        if (
          isScreenMoreThanMaxBreakpoint ||
          (screenWidth >= breakpoint &&
            lessThenNextBreakpoint() &&
            options?.navigation)
        ) {
          return { ...acc, ...(options.navigation as object) };
        }

        return acc;
      },
      { ...navigation },
    );
  }, [screenWidth]);

  if (!config?.enabled) {
    return null;
  }

  switch (type) {
    case NavigationTypes.COMPACT:
      return (
        <div
          className={clsx(styles.compactNavigationContainer, classes.container)}
        >
          <IconSVG
            type="arrowLongLeft"
            className={clsx(styles.prevCompact, classes.prev)}
          />
          <IconSVG
            type="arrowLongRight"
            className={clsx(styles.nextCompact, classes.next)}
          />
        </div>
      );
    default:
      return (
        !config?.prevEl &&
        !config?.nextEl && (
          <>
            <IconSVG
              type="arrowVerticalLeft"
              className={clsx(styles.prev, classes.prev)}
            />
            <IconSVG
              type="arrowVerticalRight"
              className={clsx(styles.next, classes.next)}
            />
          </>
        )
      );
  }
};
